<script lang="ts" context="module">
  import { browser } from '$app/environment';
  import { page } from '$app/stores';

  export type RaceAlertToastProps = Toast & { data: EventAlertsResponse };

  export const audio = browser ? new Audio() : undefined;
</script>

<script lang="ts">
  import type { EventAlertsResponse, RacesResponse } from '$types/races';
  import { gotoRace, gotoRaceById } from '$utils/navigation';
  import { onMount } from 'svelte';
  import type { Toast } from 'svelte-french-toast';
  import toast_ from 'svelte-french-toast';

  $: ({ races, user } = $page.data);

  export let toast: RaceAlertToastProps;
  $: alert = toast?.data?.data?.eventAlerts ?? undefined;

  //   find race
  $: race = $races?.data?.races.find((r) => r.id === alert?.eventId);

  const sendNotification = () => {
    if (!race) return;
    const img = '/logo-icon.webp';
    const notification = new Notification(
      `R${race.number} ${race.meeting?.track}`,
      {
        body: notificationMsg(),
        icon: img
      }
    );
    notification.onclick = () => {
      if (alert?.eventId)
        window.open('/app/racing/id/' + alert.eventId, '_blank');
    };
  };

  $: msg = () => {
    if (alert?.trigger === 'TIME_TO_JUMP') {
      const ttj = parseInt(alert?.value);
      if (ttj > -1) {
        if (ttj === 7200) return '2 hours to jump';
        if (ttj === 1800) return '30 minutes to jump';
        if (ttj === 300) return '5 minutes to jump';
        if (ttj === 60) return '1 minute to jump';
        return `${ttj} seconds to jump`;
      } else {
        return 'closed';
      }
    } else if (alert?.trigger === 'PRICE_ALERT') {
      const bold = alert.value.replace(/\*(.*?)\*/g, '<b>$1</b>');
      return bold;
    } else if (alert?.trigger === 'EVENT_ADDED') {
      // remove any markdown bold from message
      return alert.value.replace(/\*(.*?)\*/g, '$1');
    }
  };

  $: notificationMsg = () => {
    let message = msg();
    if (alert?.trigger === 'PRICE_ALERT' && message) {
      // strip first line (event)
      message = message.split('\n').slice(1).join('\n');
      //   remove any <b> tags
      message = message.replace(/<b>/g, '').replace(/<\/b>/g, '');
      // strip empty newlines
      message = message.replace(/\n\n/g, '\n');
      return message;
    }
    return message;
  };

  onMount(() => {
    play();
    sendNotification();
  });

  function dismiss() {
    toast_.dismiss(toast.id);
  }

  async function clickRace() {
    dismiss();
    if (!race && alert?.eventId) return await gotoRaceById(alert?.eventId);
    else if (race) await gotoRace(race);
  }

  function play(src = '/event-alert.mp3') {
    if (!user?.data?.user?.alertConfig?.alertSound) return;
    if (!audio) return;
    audio.pause();
    audio.src = src;
    audio.play();
  }
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  on:click={clickRace}
  on:keypress={clickRace}
  class="flex cursor-pointer flex-col p-1 hover:rounded-md hover:bg-gray-100"
>
  {#if alert?.trigger === 'TIME_TO_JUMP'}
    {#if race}
      <span class="pb-0.5 font-semibold"
        >R{race.number} {race.meeting?.track}</span
      >
      <span>{msg()}</span>
    {:else}
      <span class="pb-0.5 font-semibold">Unknown Race</span>
      {msg()}
    {/if}
  {:else if alert?.trigger === 'PRICE_ALERT'}
    <span class="pb-0.5 font-semibold">Price Alert</span>
    <span>{@html msg()}</span>
  {:else if alert?.trigger === 'EVENT_ADDED'}
    <span class="pb-0.5 font-semibold">Event Added</span>
    <span>{@html msg()}</span>
  {/if}
</div>
